import { autoinject, bindable, computedFrom } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { State } from '../../services/global-state';
import { HPFServiceClient } from '../../services/hpfApi';
import { Bucket } from '../../models/bucket';
import { ValidationController, ValidationRules } from 'aurelia-validation';
import { Uuid } from '../../helpers/uuid';

@autoinject
export class SelectBucket {
	globalState: State;
	hpfServiceClient: HPFServiceClient;
	isBucketEnabled: boolean;
	buckets: Array<Bucket>;
	@bindable selectedBucket: Bucket;
	isBucketRequired: boolean;
	label: string;
	select: string;
	errorMessage: string;
	@bindable isRenderable: boolean;
	@bindable validationController: ValidationController;
	@bindable model: any;
	uuid: string;
	@bindable showConsumerLocationDisplayName: boolean = false;

	defaultOptionItem: Bucket = {
		bucketKey: -999,
		businessKey: -1,
		consumerDisplayName: '--- PLEASE SELECT ---',
		displayName: '--- PLEASE SELECT ---',
		isActive: false,
		referenceId: ''
	};

	constructor(state: State, hpfServiceClient: HPFServiceClient, private i18n: I18N) {
		this.globalState = state;
		this.hpfServiceClient = hpfServiceClient;
		this.uuid = Uuid.next();
		this.label = this.i18n.i18next.t('location', { defaultValue: 'Location' });
		this.select = this.i18n.i18next.t('select', { defaultValue: '--Select--' });

		this.buckets = new Array<Bucket>();
	}

	attached() {
		this.isBucketEnabled = this.globalState.business.portalSettings.businessSettings.isBucketEnabled;

		this.isBucketRequired = this.isBucketEnabled;

		this.getBuckets();

		this.configureValidationRules();
	}

	@computedFrom('buckets', 'getBuckets()')
	get bucketList() : Array<Bucket> {
		return this.buckets;
	}
	@computedFrom('bucketList')
	get bucketDescriptions(): string[] {
		return this.bucketList.map(b => this.showConsumerLocationDisplayName ? b.consumerDisplayName : b.displayName).sort();
	}

	getBuckets() {
		this.hpfServiceClient.getBuckets()
			.then(response => {
				if (!response.isSuccessful) {
					return;
				}

				this.buckets = response.value;

				// When there is only one bucket, select it and hide the component.
				if (this.buckets.length === 1) {
					this.selectedBucket = this.buckets[0];
				}

				this.isRenderable = this.isBucketEnabled && this.buckets.length > 1;

				if (this.isRenderable) {
					this.buckets = this.buckets.sort((a, b) => this.sortItems(a, b, this.showConsumerLocationDisplayName));
					this.buckets.unshift(this.defaultOptionItem);
					this.selectedBucket = this.buckets[0];
				}
			})
			.catch(response => {
				// TODO:
				// Is there an area to display errors?
				// Do we want to display an error when buckets cannot be fetched?
				// couldNotRetrieveLocations,Could not retrieve locations.
				this.errorMessage = this.i18n.i18next.t('couldNotRetrieveLocations', {
					defaultValue: 'Could not retrieve locations.'
				});
			});
	}

	validate(errors) {
		if (this.isBucketRequired && (!this.selectedBucket || this.selectedBucket.bucketKey === this.defaultOptionItem.bucketKey)) {
			errors.push(
				this.i18n.i18next.t('pleaseSelectASomething', {
					defaultValue: `Please select a ${this.label}.`,
					x: this.label
				})
			);
		}
	}

	configureValidationRules() {
		if (this.isBucketRequired) {
			ValidationRules
				.ensure('selectedBucket').displayName(this.label)
				.required()
				.on(this);
		}
	}

	sortItems(a, b, showDisplayName) {
		const optionADisplayName = showDisplayName ? (a.consumerDisplayName ?? a.displayName) : a.displayName;
		const optionBDisplayName = showDisplayName ? (b.consumerDisplayName ?? b.displayName) : b.displayName;
		if (optionADisplayName < optionBDisplayName) {
			return -1;
		}
		if (optionADisplayName > optionBDisplayName) {
			return 1;
		}
		return 0;
	}
}